import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

const MIN_THUMB_WIDTH = 24;
const TRACK_INSET = 3;
const DRAG_CLICK_THRESHOLD = 6;
const READY_CLASS = "car-summary-extras-layout__custom-scroll--ready";
const DRAGGING_CLASS = "car-summary-extras-layout__custom-scroll--dragging";

const CONTENT_DRAG_SKIP_SELECTOR =
  "a[href], button, input, textarea, select, label, summary, [role='button'], [contenteditable='true'], [data-no-drag-scroll]";

const DRAG_THROUGH_SELECTOR = "[data-drag-scroll-click]";

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

export class CustomHorizontalScroll implements Plugin {
  private scroller!: HTMLElement;
  private bar!: HTMLElement;
  private thumb!: HTMLElement;

  private dragState: {
    pointerId: number;
    startX: number;
    startScrollLeft: number;
  } | null = null;
  private previousInlineSnapType = "";

  private contentDrag: {
    pointerId: number;
    startX: number;
    startScrollLeft: number;
    captured: boolean;
  } | null = null;
  private snapBeforeContentDrag = "";

  private resizeObserver: ResizeObserver | null = null;

  private readonly syncThumb = () => {
    const { maxScroll, maxThumb, thumbWidth } = this.getMetrics();
    this.thumb.style.width = `${thumbWidth}px`;

    if (maxScroll <= 0 || maxThumb <= 0) {
      this.thumb.style.transform = "translateX(0)";
      this.root.classList.remove(READY_CLASS);
      return;
    }

    const progress = clamp(this.scroller.scrollLeft / maxScroll, 0, 1);
    const thumbLeft = progress * maxThumb;
    this.thumb.style.transform = `translateX(${thumbLeft}px)`;
    this.root.classList.add(READY_CLASS);
  };

  private readonly onScroll = () => this.syncThumb();

  private readonly onWindowResize = () => this.syncThumb();

  private readonly onThumbPointerDown = (event: PointerEvent) => {
    const { maxScroll, maxThumb } = this.getMetrics();
    if (maxScroll <= 0 || maxThumb <= 0) return;

    this.dragState = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startScrollLeft: this.scroller.scrollLeft,
    };

    this.previousInlineSnapType = this.scroller.style.scrollSnapType;
    this.scroller.style.scrollSnapType = "none";
    this.thumb.setPointerCapture(event.pointerId);
    this.root.classList.add(DRAGGING_CLASS);
    event.preventDefault();
  };

  private readonly onThumbPointerMove = (event: PointerEvent) => {
    if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;
    const { maxScroll, maxThumb } = this.getMetrics();
    if (maxScroll <= 0 || maxThumb <= 0) return;

    const deltaX = event.clientX - this.dragState.startX;
    const nextScroll =
      this.dragState.startScrollLeft + (deltaX * maxScroll) / maxThumb;
    this.scroller.scrollLeft = clamp(nextScroll, 0, maxScroll);
  };

  private readonly endThumbDrag = (event: PointerEvent) => {
    if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;
    this.dragState = null;
    this.scroller.style.scrollSnapType = this.previousInlineSnapType;
    this.root.classList.remove(DRAGGING_CLASS);
  };

  private readonly onThumbLostCapture = () => {
    this.dragState = null;
    this.scroller.style.scrollSnapType = this.previousInlineSnapType;
    this.root.classList.remove(DRAGGING_CLASS);
  };

  private readonly onScrollerPointerDown = (event: PointerEvent) => {
    if (this.dragState) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;
    const el = event.target;
    const clickTarget =
      el instanceof Element ? el.closest(DRAG_THROUGH_SELECTOR) : null;
    if (
      !clickTarget &&
      el instanceof Element &&
      el.closest(CONTENT_DRAG_SKIP_SELECTOR)
    )
      return;

    const { maxScroll } = this.getMetrics();
    if (maxScroll <= 0) return;

    this.contentDrag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startScrollLeft: this.scroller.scrollLeft,
      captured: false,
    };
    this.snapBeforeContentDrag = this.scroller.style.scrollSnapType;

    if (!clickTarget) {
      this.beginContentDragCapture(event);
    }
  };

  private beginContentDragCapture(event: PointerEvent) {
    if (!this.contentDrag) return;
    this.contentDrag.captured = true;
    this.scroller.style.scrollSnapType = "none";
    try {
      this.scroller.setPointerCapture(event.pointerId);
    } catch {
      /* noop */
    }
    this.root.classList.add(DRAGGING_CLASS);
    event.preventDefault();
  }

  private readonly onContentPointerMove = (event: PointerEvent) => {
    if (!this.contentDrag || event.pointerId !== this.contentDrag.pointerId)
      return;
    const { maxScroll } = this.getMetrics();
    if (maxScroll <= 0) return;
    const deltaX = event.clientX - this.contentDrag.startX;

    if (!this.contentDrag.captured) {
      if (Math.abs(deltaX) <= DRAG_CLICK_THRESHOLD) return;
      this.beginContentDragCapture(event);
    }

    this.scroller.scrollLeft = clamp(
      this.contentDrag.startScrollLeft - deltaX,
      0,
      maxScroll,
    );
    event.preventDefault();
  };

  private readonly endContentDrag = (event: PointerEvent) => {
    if (!this.contentDrag || event.pointerId !== this.contentDrag.pointerId)
      return;
    const { captured } = this.contentDrag;
    this.contentDrag = null;
    if (!captured) return;

    this.scroller.style.scrollSnapType = this.snapBeforeContentDrag;
    this.root.classList.remove(DRAGGING_CLASS);
    try {
      this.scroller.releasePointerCapture(event.pointerId);
    } catch {
      /* noop */
    }
  };

  private readonly onScrollerLostCapture = (event: PointerEvent) => {
    if (this.contentDrag && event.pointerId === this.contentDrag.pointerId) {
      this.contentDrag = null;
      this.scroller.style.scrollSnapType = this.snapBeforeContentDrag;
      this.root.classList.remove(DRAGGING_CLASS);
    }
  };

  private readonly onBarPointerDown = (event: PointerEvent) => {
    if (event.target === this.thumb) return;

    const rect = this.bar.getBoundingClientRect();
    const { maxScroll, maxThumb, thumbWidth } = this.getMetrics();
    if (maxScroll <= 0 || maxThumb <= 0) return;

    const clickX = event.clientX - rect.left;
    const clickXInTrack = clamp(clickX - TRACK_INSET, 0, maxThumb + thumbWidth);
    const thumbLeft = clamp(clickXInTrack - thumbWidth / 2, 0, maxThumb);
    const progress = thumbLeft / maxThumb;
    this.scroller.scrollTo({ left: progress * maxScroll, behavior: "smooth" });
  };

  constructor(private root: HTMLElement) {}

  init(): void {
    const scroller = this.root.querySelector("[data-custom-scroll-content]");
    const bar = this.root.querySelector("[data-custom-scrollbar]");
    const thumb = this.root.querySelector("[data-custom-scroll-thumb]");

    if (
      !(scroller instanceof HTMLElement) ||
      !(bar instanceof HTMLElement) ||
      !(thumb instanceof HTMLElement)
    ) {
      return;
    }

    this.scroller = scroller;
    this.bar = bar;
    this.thumb = thumb;

    this.scroller.addEventListener("scroll", this.onScroll, { passive: true });
    window.addEventListener("resize", this.onWindowResize, { passive: true });

    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(this.syncThumb);
      this.resizeObserver.observe(this.scroller);
      this.resizeObserver.observe(this.bar);
    }

    this.thumb.addEventListener("pointerdown", this.onThumbPointerDown);
    this.thumb.addEventListener("pointermove", this.onThumbPointerMove);
    this.thumb.addEventListener("pointerup", this.endThumbDrag);
    this.thumb.addEventListener("pointercancel", this.endThumbDrag);
    this.thumb.addEventListener("lostpointercapture", this.onThumbLostCapture);

    this.scroller.addEventListener("pointerdown", this.onScrollerPointerDown);
    this.scroller.addEventListener("pointermove", this.onContentPointerMove);
    this.scroller.addEventListener("pointerup", this.endContentDrag);
    this.scroller.addEventListener("pointercancel", this.endContentDrag);
    this.scroller.addEventListener(
      "lostpointercapture",
      this.onScrollerLostCapture,
    );

    this.bar.addEventListener("pointerdown", this.onBarPointerDown);

    this.syncThumb();
  }

  destroy(): void {
    this.scroller?.removeEventListener("scroll", this.onScroll);
    window.removeEventListener("resize", this.onWindowResize);

    this.resizeObserver?.disconnect();
    this.resizeObserver = null;

    this.thumb?.removeEventListener("pointerdown", this.onThumbPointerDown);
    this.thumb?.removeEventListener("pointermove", this.onThumbPointerMove);
    this.thumb?.removeEventListener("pointerup", this.endThumbDrag);
    this.thumb?.removeEventListener("pointercancel", this.endThumbDrag);
    this.thumb?.removeEventListener(
      "lostpointercapture",
      this.onThumbLostCapture,
    );

    this.scroller?.removeEventListener(
      "pointerdown",
      this.onScrollerPointerDown,
    );
    this.scroller?.removeEventListener(
      "pointermove",
      this.onContentPointerMove,
    );
    this.scroller?.removeEventListener("pointerup", this.endContentDrag);
    this.scroller?.removeEventListener("pointercancel", this.endContentDrag);
    this.scroller?.removeEventListener(
      "lostpointercapture",
      this.onScrollerLostCapture,
    );

    this.bar?.removeEventListener("pointerdown", this.onBarPointerDown);

    this.root.classList.remove(READY_CLASS, DRAGGING_CLASS);
  }

  private getMetrics() {
    const maxScroll = Math.max(
      this.scroller.scrollWidth - this.scroller.clientWidth,
      0,
    );
    const barTrackWidth = Math.max(this.bar.clientWidth - TRACK_INSET * 2, 0);
    const proportionalWidth =
      maxScroll > 0 && barTrackWidth > 0
        ? (this.scroller.clientWidth / this.scroller.scrollWidth) *
          barTrackWidth
        : barTrackWidth;
    const thumbWidth = clamp(
      proportionalWidth,
      MIN_THUMB_WIDTH,
      barTrackWidth || MIN_THUMB_WIDTH,
    );
    const maxThumb = Math.max(barTrackWidth - thumbWidth, 0);
    return { maxScroll, maxThumb, thumbWidth };
  }
}

PluginRegistry.register("customHorizontalScroll", CustomHorizontalScroll);
